import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";


const NAV = [
    { label: "How It Works", hash: "#how-it-works" },
    { label: "Projects", hash: "#our-work" },
    { label: "FAQs", hash: "#faqs" },
    { label: "Get Your Free Design", hash: "#enquiry-form", cta: true },
];


export default function Header() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const onHome = pathname === "/";
    const [solid, setSolid] = useState(!onHome);
    const [menuOpen, setMenuOpen] = useState(false);


    // On home: transparent at top, solid as soon as user scrolls past 8px.
    // On legal pages: always solid.
    useEffect(() => {
        if (!onHome) { setSolid(true); return; }
        setSolid(window.scrollY > 8);
        let raf = null;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                setSolid(window.scrollY > 8);
                raf = null;
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [onHome]);


    // Lock body scroll while mobile menu is open.
    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);


    const goTo = (hash) => {
        setMenuOpen(false);
        if (onHome) {
            const el = document.querySelector(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate("/" + hash);
        }
    };


    return (
        <>
            <header
                data-testid="site-header"
                className={
                    "fixed top-0 left-0 right-0 z-50 transition-all duration-300 " +
                    (solid ? "bg-charcoal/70 backdrop-blur-md border-b border-white/10" : "bg-transparent")
                }
            >
                <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 h-[64px] md:h-[76px]">
                    <Link to="/" onClick={() => window.scrollTo({ top: 0 })} data-testid="header-logo-link" aria-label="London Renovation Specialists — home">
                        <Logo brand="lrs" />
                    </Link>
                    <nav className="hidden md:flex items-center gap-8">
                        {NAV.slice(0, 3).map((n) => (
                            <button
                                key={n.hash}
                                onClick={() => goTo(n.hash)}
                                className="font-manrope font-semibold text-[16px] tracking-wide transition-colors text-offwhite hover:text-terracotta"
                                data-testid={`nav-${n.hash.slice(1)}`}
                            >
                                {n.label}
                            </button>
                        ))}
                        <button
                            onClick={() => goTo("#enquiry-form")}
                            className="font-manrope font-bold text-[15px] px-6 py-3 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 hover:-translate-y-0.5 transition-all"
                            data-testid="nav-cta"
                        >
                            Get Your Free Design
                        </button>
                    </nav>
                    <button
                        onClick={() => setMenuOpen((v) => !v)}
                        className="md:hidden p-2.5 -mr-2 text-offwhite"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                        data-testid="mobile-menu-toggle"
                    >
                        {menuOpen ? <X size={32} /> : <Menu size={32} />}
                    </button>
                </div>
            </header>


            {/* Mobile full-screen menu — charcoal takeover, off-white text */}
            {menuOpen && (
                <div className="fixed inset-0 z-40 bg-charcoal md:hidden pt-[64px] flex flex-col" data-testid="mobile-menu">
                    <nav className="flex-1 flex flex-col justify-center items-start px-8 gap-8">
                        {NAV.slice(0, 3).map((n) => (
                            <button
                                key={n.hash}
                                onClick={() => goTo(n.hash)}
                                className="font-manrope font-semibold text-3xl text-offwhite hover:text-terracotta text-left transition-colors"
                                data-testid={`mobile-nav-${n.hash.slice(1)}`}
                            >
                                {n.label}
                            </button>
                        ))}
                        <button
                            onClick={() => goTo("#enquiry-form")}
                            className="mt-6 font-manrope font-bold text-base px-6 py-3 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 transition-colors shadow-lg"
                            data-testid="mobile-nav-enquiry-form"
                        >
                            Get Your Free Design
                        </button>
                    </nav>
                </div>
            )}
        </>
    );
}
